import { camelCaseToCapitalCase, TreeSelectDropDown } from '@replai-platform/ui-components';
import * as SDK from '@replai-platform/sdk';
import { useMemo } from 'react';
import { capitalize } from '@material-ui/core';

export interface CustomTagsFilterProps {
  options: DropdownOption[];
  onChange: (params: string[]) => void;
  loading: boolean;
  /** 'exclude' labels the chip for a filter whose selected tags are blacklisted rather than required */
  variant?: 'select' | 'exclude';
}

type DropdownOption = { id: string | null; type: string | null; value: string | null; selected: boolean };
function serializeEntry(entry: DropdownOption) {
  return JSON.stringify({ id: entry.id, type: entry.type, value: entry.value });
}

const CustomTagsFilter: React.VFC<CustomTagsFilterProps> = ({ options, onChange, loading, variant = 'select' }) => {
  const { data, selected } = useMemo(
    () => ({
      data: options
        .reduce((acc, curr) => {
          const idx = acc.findIndex(({ type }) => type === curr.type);
          const element = {
            key: serializeEntry(curr),
            title: curr.value ? capitalize(curr.value) : SDK.messages.NOT_AVAILABLE,
          };
          if (idx >= 0) {
            acc[idx].children.push(element);
          } else {
            acc.push({ type: curr.type || SDK.messages.NOT_AVAILABLE, children: [element] });
          }
          return acc;
        }, [] as { type: string; children: { key: string; title: string }[] }[])
        .map((opt) => ({
          key: JSON.stringify(opt.type),
          title: camelCaseToCapitalCase(opt.type || ''),
          children: opt.children,
        })),
      selected: options.filter((opt) => opt.selected).map((opt) => serializeEntry(opt)),
    }),
    [options]
  );

  const chipLabel = useMemo(() => {
    const action = variant === 'exclude' ? 'excluded' : 'selected';
    switch (selected.length) {
      case 0:
        return variant === 'exclude' ? 'Exclude Tags' : 'Select Tags';
      case 1:
        return `${selected.length} tag ${action}`;
      default:
        return `${selected.length} tags ${action}`;
    }
  }, [selected.length, variant]);

  return (
    <TreeSelectDropDown
      data={data}
      label={chipLabel}
      loading={loading}
      searchable
      onChangeSelectedKeys={onChange}
      onClearClick={() => onChange([])}
      selectedKeys={selected}
    />
  );
};

export default CustomTagsFilter;
